import type { ProgressStep, Stage, V1DraftResponse } from '@aira/shared';
import { BUTTON, COMPOSE, ERRORS, STATUS } from '@aira/shared';
import { CitationList, DraftViewer, Stepper } from '@aira/ui';
import { Button, type ButtonProps } from '@fluentui/react-components';
import { RotateCw, ThumbsDown, ThumbsUp } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { ProgressGroup } from '../api/ragBackend';
import { StageProgressDetails } from './StageProgressDetails';

type Feedback = 'like' | 'dislike' | null;

const AIRA_EMAIL_URL = import.meta.env.VITE_AIRA_EMAIL_URL as string | undefined;

/**
 * Prefer the backend's Sharefox Admin deep link (`knowledge_base_url`) —
 * same host/session as "Update Knowledge", no separate aira-email sign-in.
 * Falls back to the standalone aira-email app only when the tenant has no
 * known admin site yet (e.g. not activated through Sharefox Admin).
 */
function openAddKnowledge(knowledgeBaseUrl?: string | null) {
  if (knowledgeBaseUrl) {
    window.open(knowledgeBaseUrl, '_blank', 'noopener');
    return;
  }
  if (!AIRA_EMAIL_URL) return;
  window.open(`${AIRA_EMAIL_URL}/resources?addKnowledge=1`, '_blank', 'noopener');
}

export interface ComposeDraftResultProps {
  response: V1DraftResponse;
  stepperStages?: ComponentProps<typeof Stepper>['stages'];
  progressSteps?: ProgressStep[];
  progressGroups?: ProgressGroup[];
  draftText: string;
  onDraftChange?: (text: string) => void;
  primaryLabel?: string;
  /** Always-visible grey helper text under the primary button (e.g. the Outlook
   *  mobile Copy & Close instruction). Shown regardless of clicks. */
  primaryHelpText?: string | null;
  /** Transient inline notice under the button (e.g. a mobile copy-failed
   *  fallback). Distinct from the always-on primaryHelpText. */
  primaryNotice?: string | null;
  onPrimary: () => void;
  onRefine: (note: string) => void;
  isRefining?: boolean;
  regenerateCount: number;
  feedback?: Feedback;
  feedbackSaving?: boolean;
  feedbackError?: string | null;
  onFeedback?: (feedback: Exclude<Feedback, null>) => void;
  /** Persist an optional free-text note after a thumb click (Send). */
  onFeedbackNote?: (note: string) => void;
  feedbackNoteSaving?: boolean;
  /** Render the "Is this helpful?" controls on their own row below the button /
   *  helper text instead of inline on the button row (Outlook mobile layout). */
  feedbackBelow?: boolean;
  onStartOver?: () => void;
}

export function ComposeDraftResult({
  response,
  stepperStages,
  progressSteps,
  progressGroups,
  draftText,
  onDraftChange,
  primaryLabel = BUTTON.insertIntoReply,
  primaryHelpText = null,
  primaryNotice = null,
  onPrimary,
  onRefine,
  isRefining = false,
  regenerateCount,
  feedback = null,
  feedbackSaving = false,
  feedbackError = null,
  onFeedback,
  onFeedbackNote,
  feedbackNoteSaving = false,
  feedbackBelow = false,
  onStartOver,
}: ComposeDraftResultProps) {
  const [note, setNote] = useState('');
  const [feedbackNote, setFeedbackNote] = useState('');
  const [feedbackToastOpen, setFeedbackToastOpen] = useState(false);
  const [feedbackToastKind, setFeedbackToastKind] = useState<'thumb' | 'note'>('thumb');
  const [selectedProgressStage, setSelectedProgressStage] = useState<Stage | null>(null);
  const previousTraceIdRef = useRef(response.trace_id);
  const previousFeedbackRef = useRef<Feedback>(feedback);
  const refineDisabled = regenerateCount >= 3;
  const controlsDisabled = isRefining;
  const refineTitle = refineDisabled ? ERRORS.regenerateLimitReached : BUTTON.refine;
  const showFeedbackNote = Boolean(feedback && onFeedbackNote);
  const feedbackNotePlaceholder =
    feedback === 'dislike'
      ? COMPOSE.feedbackNotePlaceholderDislike
      : COMPOSE.feedbackNotePlaceholderLike;

  const submitRefine = (nextNote: string) => {
    const trimmed = nextNote.trim();
    if (!trimmed || refineDisabled || controlsDisabled) return;
    setNote('');
    onRefine(trimmed);
  };

  const submitFeedbackNote = () => {
    if (!onFeedbackNote || !feedback || feedbackNoteSaving || feedbackSaving || controlsDisabled) {
      return;
    }
    setFeedbackToastKind('note');
    setFeedbackToastOpen(true);
    onFeedbackNote(feedbackNote);
  };

  const feedbackButton = (
    kind: Exclude<Feedback, null>,
    icon: ButtonProps['icon'],
    label: string,
  ) => (
    <Button
      appearance={feedback === kind ? 'primary' : 'subtle'}
      icon={icon}
      size="small"
      title={label}
      aria-label={label}
      onClick={() => {
        setFeedbackToastKind('thumb');
        setFeedbackToastOpen(true);
        onFeedback?.(kind);
      }}
      disabled={!onFeedback || feedbackSaving || feedbackNoteSaving || controlsDisabled}
    />
  );

  const feedbackTitle = feedbackError
    ? 'Feedback not saved'
    : feedbackSaving || feedbackNoteSaving
      ? 'Saving feedback'
      : feedbackToastKind === 'note'
        ? BUTTON.send
        : feedback === 'dislike'
          ? BUTTON.dislike
          : BUTTON.like;
  const feedbackMessage = feedbackError
    ? feedbackError
    : feedbackSaving || feedbackNoteSaving
      ? 'Saving your feedback...'
      : feedbackToastKind === 'note'
        ? feedbackNote.trim()
          ? STATUS.feedbackNoteSaved
          : feedback === 'dislike'
            ? STATUS.feedbackNegative
            : STATUS.feedbackPositive
        : feedback === 'dislike'
          ? STATUS.feedbackNegative
          : STATUS.feedbackPositive;
  const feedbackTone = feedbackError
    ? 'border-red-200 bg-red-50 text-red-700'
    : 'border-sf-primary/20 bg-sf-dark text-white';

  useEffect(() => {
    if (!feedbackToastOpen || feedbackSaving || feedbackNoteSaving) return;
    const timeout = window.setTimeout(() => setFeedbackToastOpen(false), 2600);
    return () => window.clearTimeout(timeout);
  }, [feedbackToastOpen, feedbackSaving, feedbackNoteSaving]);

  useEffect(() => {
    if (previousTraceIdRef.current !== response.trace_id) {
      previousTraceIdRef.current = response.trace_id;
      setSelectedProgressStage(null);
      setFeedbackNote('');
    }
  }, [response.trace_id]);

  useEffect(() => {
    if (previousFeedbackRef.current !== feedback) {
      previousFeedbackRef.current = feedback;
      // Clear stale note text when the merchant flips thumbs so the placeholder matches.
      setFeedbackNote('');
    }
  }, [feedback]);

  const renderFeedbackControls = (extraClass = '') => (
    <span className={`flex items-center gap-2 ${extraClass}`}>
      <span className="text-xs text-sf-gray">{COMPOSE.feedbackPrompt}</span>
      <span className="flex gap-1">
        {feedbackButton('like', <ThumbsUp className="h-4 w-4" />, BUTTON.like)}
        {feedbackButton('dislike', <ThumbsDown className="h-4 w-4" />, BUTTON.dislike)}
      </span>
    </span>
  );

  return (
    <div className="flex flex-col gap-3">
      <DraftViewer
        text={draftText}
        isStreaming={false}
        editable={Boolean(onDraftChange) && !controlsDisabled}
        onChange={controlsDisabled ? undefined : onDraftChange}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onPrimary}
            disabled={controlsDisabled}
            className="rounded-lg px-4 py-2 text-sm font-semibold bg-sf-primary text-sf-secondary hover:bg-sf-primary-hover transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            {primaryLabel}
          </button>
          {onStartOver && (
            <Button
              appearance="subtle"
              size="small"
              onClick={onStartOver}
              disabled={controlsDisabled}
            >
              {BUTTON.startOver}
            </Button>
          )}
        </span>
        {!feedbackBelow && renderFeedbackControls('ml-auto')}
      </div>

      {/* Outlook mobile: always-on instruction explaining Copy & Close. */}
      {primaryHelpText && <p className="-mt-1 text-xs text-sf-gray">{primaryHelpText}</p>}

      {primaryNotice && (
        <p role="status" aria-live="polite" className="-mt-1 text-xs font-medium text-sf-primary">
          {primaryNotice}
        </p>
      )}

      {/* Mobile layout moves feedback below the button + helper text. */}
      {feedbackBelow && <div className="flex">{renderFeedbackControls()}</div>}

      {showFeedbackNote && (
        <div className="flex flex-col gap-2">
          <label htmlFor="feedback-note" className="sr-only">
            {feedbackNotePlaceholder}
          </label>
          <textarea
            id="feedback-note"
            value={feedbackNote}
            onChange={(e) => setFeedbackNote(e.target.value)}
            placeholder={feedbackNotePlaceholder}
            rows={2}
            maxLength={2000}
            disabled={feedbackSaving || feedbackNoteSaving || controlsDisabled}
            className="w-full resize-y rounded-md border border-sf-primary/20 bg-sf-surface px-3 py-2 text-sm text-sf-dark placeholder:text-sf-gray focus:outline-none focus:ring-2 focus:ring-sf-primary disabled:opacity-50"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              appearance="primary"
              size="small"
              disabled={feedbackSaving || feedbackNoteSaving || controlsDisabled}
              onClick={submitFeedbackNote}
              className="!bg-sf-primary hover:!bg-sf-primary-hover"
            >
              {feedbackNoteSaving ? 'Sending…' : BUTTON.send}
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2 rounded-lg border border-sf-primary/15 bg-sf-primary-light/60 p-3">
        <label htmlFor="refine-note" className="text-[10px] uppercase tracking-wide text-sf-gray">
          Refine the reply
        </label>
        <textarea
          id="refine-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add your instructions..."
          rows={2}
          maxLength={500}
          disabled={refineDisabled || controlsDisabled}
          className="w-full resize-y rounded-md border border-sf-primary/20 bg-sf-surface px-3 py-2 text-sm text-sf-dark placeholder:text-sf-gray focus:outline-none focus:ring-2 focus:ring-sf-primary disabled:opacity-50"
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            appearance="primary"
            size="small"
            icon={<RotateCw className={`h-4 w-4 ${isRefining ? 'animate-spin' : ''}`} />}
            disabled={refineDisabled || controlsDisabled || !note.trim()}
            title={refineTitle}
            onClick={() => submitRefine(note)}
          >
            {isRefining ? 'Refining…' : BUTTON.refine}
          </Button>
        </div>
      </div>

      <CitationList
        citations={response.citations}
        onAddKnowledge={
          response.knowledge_base_url || AIRA_EMAIL_URL
            ? () => openAddKnowledge(response.knowledge_base_url)
            : undefined
        }
      />

      {stepperStages && (
        <>
          <Stepper
            stages={stepperStages}
            selectedStage={selectedProgressStage}
            onStageClick={
              progressSteps
                ? (stage) =>
                    setSelectedProgressStage((current) =>
                      current === stage.stage ? null : stage.stage,
                    )
                : undefined
            }
          />
          {selectedProgressStage && progressSteps && (
            <StageProgressDetails
              stage={selectedProgressStage}
              steps={progressSteps}
              groups={progressGroups}
              onClose={() => setSelectedProgressStage(null)}
            />
          )}
        </>
      )}

      {feedbackToastOpen && (
        <div className="pointer-events-none fixed inset-x-3 top-4 z-[1000] flex justify-center">
          <div
            role="status"
            aria-live="polite"
            className={`pointer-events-auto max-w-sm rounded-md border px-3 py-2 text-sm shadow-lg ${feedbackTone}`}
          >
            <p className="font-semibold leading-snug">{feedbackTitle}</p>
            <p className="mt-0.5 text-xs leading-snug opacity-90">{feedbackMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
}
